import { log } from '@/log'
import { CartItem, CartItemList } from '@/models/cart/cart-item'
import { ErrorResponseBody } from '@spa/services/order'
import _ from 'lodash'

/*
 * カート整合性エラーが発生した時のエラーメッセージ一覧.
 * 他のエラーメッセージ構築でも流用できそう？
 */

export const errorKind = 'InconsistentCartOnPurchaseError'

interface ErrorMessageConfig<T> {
  matchCartItem: (cartItem: CartItem, compared: T) => boolean,
  buildMessage: (cartItem: CartItem) => string,
}

/*
 * Map生成時に配列を渡して初期化する方法は、IE非サポート.
 * そのため、一つ一つsetで設定している.
 * @see https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/Map#browser_compatibility
 */
const configMap: Map<string, ErrorMessageConfig<string>> = new Map()

configMap.set('MustWithinRootPurchaseLimit', {
  matchCartItem: (cartItem: CartItem, rootId: string) => cartItem.rootId === rootId,
  buildMessage: (cartItem: CartItem) =>
    `${cartItem.name} ${cartItem.color} は購入上限数に達しているため、ご注文手続きへ進めません。対象の商品を削除いただき、ご注文手続きへお進みください｡`,
})
configMap.set('MustWithinSKUPurchaseLimit', {
  matchCartItem: (cartItem: CartItem, skuId: string) => cartItem.skuId === skuId,
  buildMessage: (cartItem: CartItem) =>
    `${cartItem.name} ${cartItem.color} は購入上限数に達しているため、ご注文手続きへ進めません。対象の商品を削除いただき、ご注文手続きへお進みください｡`,
})
configMap.set('MustWithinLimitTimesOfPurchase', {
  matchCartItem: (cartItem: CartItem, fkuId: string) => cartItem.fkuId === fkuId,
  buildMessage: (cartItem: CartItem) =>
    `${cartItem.name} ${cartItem.color} は購入回数の上限に達しているため、ご注文手続きへ進めません。対象の商品を削除いただき、ご注文手続きへお進みください｡`,
})

/**
 * エラーレスポンスに含まれるエラーメッセージとして、想定しているパターン.
 *
 * @example
 * [InconsistentCartOnPurchaseError][MustWithinSKUPurchaseLimit][${エラー原因のSKU ID}] ${message}
 * @example
 * [InconsistentCartOnPurchaseError][MustWithinRootPurchaseLimit][${エラー原因のROOT ID}] ${message}
 * @example
 * [InconsistentCartOnPurchaseError][MustWithinLimitTimesOfPurchase][${エラー原因のFKU ID}] ${message}
 */
const textPattern = new RegExp(`\\[${errorKind}\]\\[(.+)\]\\[(.+)\]`)
function buildMessageFromErrorText(errorText: string, carts: CartItemList): string[] {
  const result = errorText.match(textPattern)
  if (result === null)  {
    log.warn(`error message "${errorText}" is unexpected pattern`)

    return []
  }

  const [, key, itemId] = result

  const config = configMap.get(key)
  if (!itemId) {
    log.warn(`could not find invalid item id from ${errorText}`)

    return []
  }
  if (!config) {
    log.warn(`error text is ${errorText}`)
    log.warn(`could not find error message config for ${key}`)

    return []
  }

  return carts.filter(c => config.matchCartItem(c, itemId)).map(config.buildMessage)
}

/**
 * /api/${brand.english_name/commerce_purchase
 * /api/commerce_purchase
 *
 * 上記APIリクエストがカート不整合でエラーになった場合に、
 * そのエラーレスポンス情報からエラーメッセージを構築する関数.
 *
 * @param error APIエラーレスポンスのbody
 * @param cartList 購入しようとしているカート内商品の一覧
 *
 * @see https://github.com/my-color/front/pull/5074
 */
export function buildErrorMessages(error: ErrorResponseBody, cartList: CartItemList): string[] {
  /*
   * 同じROOT/FKUに対するエラーメッセージは一つにまとめて表示する(ユーザーに示す情報がROOT/FKUまでのため).
   * ここでは、ROOTが異なるなら商品名は異なるということを前提にしている.
   * そのため、ROOTが異なっていても商品名が一致している場合、エラーメッセージはまとめられてしまう
   *
   * しかし、そこをより厳密に区別してID単位で出力仕分けても、ユーザーに見える内容は同一であり、単なる重複にしか見えない.
   * なので、ここでは「ROOTが異なっていても商品名が一致している」というケースは運用ミスと割り切っている.
   */
  return _.uniq(
    _.flatMap(error.cause, text => buildMessageFromErrorText(text, cartList))
  )
}
